import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// Nearly every real bid lands in here. Anything higher goes through the
// keyboard, which is the rare case and can afford the extra tap.
const QUICK_BIDS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * One-tap bid entry for touch screens: fill the bid here, commit on the BID
 * plate. Two taps, no on-screen keyboard eating the board.
 */
const useStyles = createUseStyles({
  row: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '5px',
    padding: '6px 8px 5px',
    background: '#1a0f0a',
    borderTop: '1px solid #4a3020',
    overflowX: 'auto',
    // The plates keep their width instead of squeezing on narrow screens.
    '&::-webkit-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
  },
  plate: {
    flex: '1 0 auto',
    minWidth: '44px',
    height: '42px',
    background: '#3a2416',
    border: 'none',
    borderRadius: '8px',
    color: '#f0dcc0',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: 1,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.14)',
      'inset 0 -2px 0 rgba(0,0,0,0.3)',
      '0 3px 0 #1c1108',
    ].join(','),
    transition: 'background .1s ease',
    '&:disabled': {
      opacity: 0.3,
      cursor: 'default',
      boxShadow: 'none',
    },
    '&:not(:disabled):active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 2px 5px rgba(0,0,0,0.45)',
    },
  },
  plateActive: {
    background: '#d94f2b',
    color: '#fff3e6',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -2px 0 rgba(0,0,0,0.25)',
      '0 3px 0 #7d2a12',
    ].join(','),
  },
  more: {
    fontSize: '15px',
    letterSpacing: '1px',
    color: '#c4a070',
  },
});

function BidQuickPick({ value, onPick, onMore, maxBid = null }) {
  const classes = useStyles();
  const t = useT();
  const current = parseInt(value, 10);
  // A rebid has to beat your standing bid, so anything at or above it is dead.
  const tooHigh = (n) => maxBid !== null && n >= maxBid;

  return (
    <div className={classes.row}>
      {QUICK_BIDS.map(n => (
        <button
          key={n}
          type="button"
          aria-pressed={current === n}
          disabled={tooHigh(n)}
          onClick={() => onPick(n)}
          className={`${classes.plate} ${current === n ? classes.plateActive : ''}`}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        aria-label={t('a11y.more')}
        onClick={onMore}
        className={`${classes.plate} ${classes.more}`}
      >
        ⋯
      </button>
    </div>
  );
}

export default BidQuickPick;
